/**
 * useResizable Hook
 *
 * Provides resize functionality from edges and corners for Mac Studio windows.
 */

import { useCallback, useRef, useState, useEffect } from 'react'

interface Size {
  width: number
  height: number
}

interface Position {
  x: number
  y: number
}

type ResizeDirection =
  | 'n'  // North (top)
  | 's'  // South (bottom)
  | 'e'  // East (right)
  | 'w'  // West (left)
  | 'ne' // Northeast (top-right)
  | 'nw' // Northwest (top-left)
  | 'se' // Southeast (bottom-right)
  | 'sw' // Southwest (bottom-left)

interface UseResizableOptions {
  /** Initial size */
  initialSize?: Size
  /** Minimum dimensions */
  minSize?: Size
  /** Maximum dimensions */
  maxSize?: Size
  /** Current position (needed for nw, n, ne, w, sw resize) */
  position?: Position
  /** Called when resize starts */
  onResizeStart?: (size: Size) => void
  /** Called during resize */
  onResize?: (size: Size, position?: Position) => void
  /** Called when resize ends */
  onResizeEnd?: (size: Size, position?: Position) => void
  /** Whether resizing is enabled */
  enabled?: boolean
}

interface UseResizableReturn {
  /** Props for creating resize handles */
  getResizeHandleProps: (direction: ResizeDirection) => {
    onPointerDown: (e: React.PointerEvent) => void
    style: React.CSSProperties
    'data-resize-direction': ResizeDirection
  }
  /** Current size */
  size: Size
  /** Whether currently resizing */
  isResizing: boolean
  /** Active resize direction */
  resizeDirection: ResizeDirection | null
  /** Set size programmatically */
  setSize: (size: Size) => void
}

// Cursor styles for each direction
const CURSOR_MAP: Record<ResizeDirection, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
  sw: 'nesw-resize',
}

export function useResizable({
  initialSize = { width: 400, height: 300 },
  minSize = { width: 200, height: 150 },
  maxSize = { width: Infinity, height: Infinity },
  position,
  onResizeStart,
  onResize,
  onResizeEnd,
  enabled = true,
}: UseResizableOptions = {}): UseResizableReturn {
  const [size, setSize] = useState<Size>(initialSize)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null)

  // Store resize state in refs to avoid stale closures
  const resizeStateRef = useRef({
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    startPosX: 0,
    startPosY: 0,
    direction: null as ResizeDirection | null,
  })

  // Update size when initialSize changes externally
  useEffect(() => {
    setSize(initialSize)
  }, [initialSize.width, initialSize.height])

  // Constrain size to min/max bounds
  const constrainSize = useCallback(
    (newSize: Size): Size => ({
      width: Math.max(minSize.width, Math.min(maxSize.width, newSize.width)),
      height: Math.max(minSize.height, Math.min(maxSize.height, newSize.height)),
    }),
    [minSize.width, minSize.height, maxSize.width, maxSize.height]
  )

  // Handle pointer move during resize
  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!enabled) return

      const state = resizeStateRef.current
      const { direction, startX, startY, startWidth, startHeight, startPosX, startPosY } = state
      if (!direction) return

      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      let newWidth = startWidth
      let newHeight = startHeight
      let newPosX = startPosX
      let newPosY = startPosY

      // Calculate new size based on direction
      if (direction.includes('e')) {
        newWidth = startWidth + deltaX
      }
      if (direction.includes('w')) {
        const potentialWidth = startWidth - deltaX
        if (potentialWidth >= minSize.width && potentialWidth <= maxSize.width) {
          newWidth = potentialWidth
          // Clamp position to prevent going off-screen
          newPosX = Math.max(0, startPosX + deltaX)
        }
      }
      if (direction.includes('s')) {
        newHeight = startHeight + deltaY
      }
      if (direction.includes('n')) {
        const potentialHeight = startHeight - deltaY
        if (potentialHeight >= minSize.height && potentialHeight <= maxSize.height) {
          newHeight = potentialHeight
          // Clamp position to prevent going off-screen (above menu bar)
          newPosY = Math.max(28, startPosY + deltaY)
        }
      }

      const constrainedSize = constrainSize({ width: newWidth, height: newHeight })
      setSize(constrainedSize)

      // If resizing from top or left, position may change
      const positionChanged = direction.includes('n') || direction.includes('w')
      const newPosition = positionChanged ? { x: newPosX, y: newPosY } : undefined

      onResize?.(constrainedSize, newPosition)
    },
    [constrainSize, onResize, enabled, minSize.width, minSize.height, maxSize.width, maxSize.height]
  )

  // Handle pointer up (end resize)
  const handlePointerUp = useCallback(() => {
    setIsResizing(false)
    setResizeDirection(null)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''

    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)

    const state = resizeStateRef.current
    const positionChanged = state.direction?.includes('n') || state.direction?.includes('w')
    const finalPosition = positionChanged
      ? { x: state.startPosX, y: state.startPosY }
      : undefined

    onResizeEnd?.(size, finalPosition)
  }, [handlePointerMove, onResizeEnd, size])

  // Handle pointer down (start resize)
  const handlePointerDown = useCallback(
    (direction: ResizeDirection) => (e: React.PointerEvent) => {
      if (!enabled) return
      if (e.button !== 0 && e.pointerType === 'mouse') return

      e.preventDefault()
      e.stopPropagation()

      resizeStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height,
        startPosX: position?.x ?? 0,
        startPosY: position?.y ?? 0,
        direction,
      }

      setIsResizing(true)
      setResizeDirection(direction)
      document.body.style.cursor = CURSOR_MAP[direction]
      document.body.style.userSelect = 'none'

      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)

      onResizeStart?.(size)
    },
    [size, position, handlePointerMove, handlePointerUp, onResizeStart, enabled]
  )

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [handlePointerMove, handlePointerUp])

  // Get props for a resize handle
  const getResizeHandleProps = useCallback(
    (direction: ResizeDirection) => ({
      onPointerDown: handlePointerDown(direction),
      style: {
        cursor: CURSOR_MAP[direction],
        touchAction: 'none' as const,
      },
      'data-resize-direction': direction,
    }),
    [handlePointerDown]
  )

  return {
    getResizeHandleProps,
    size,
    isResizing,
    resizeDirection,
    setSize,
  }
}
